/**
 * Auto-arrange: one tap lays out whatever the shopper already has — photos,
 * words, icons and shapes — in a tidy composition that fills the print zone.
 *
 * Unlike a template (which REPLACES the design) or a starter layout (which
 * rebuilds one photo and one text), an arrangement keeps every layer, its
 * content, crop, colours and effects, and only moves and uniformly scales it.
 * Relative sizes inside a block survive on purpose: a name stays bigger than
 * the date under it, because the shopper made it so.
 *
 * The layer set is sorted into three roles:
 *   - **photos**: uploads (and any group that contains one),
 *   - **words**:  text layers (and all-text groups), in reading order,
 *   - **decor**:  icons, emoji, shapes and QR codes.
 * Each arrangement is a recipe of bands (stacked top to bottom) or columns
 * (side by side) over those roles. A band is sized from what its content
 * would need at full width, capped so a single short word cannot swallow the
 * zone, then everything is scaled to fit and the whole composition is centred.
 *
 * Pure and atomic like every other Designer command: same input, same output,
 * validated against the product's rules before it is returned.
 */

import {
  designAreaProblems,
  layersExtent,
  normalizeDesignAnswer,
  resolveDesignAreaConfig,
  type DesignAreaAnswer,
  type DesignAreaConfig,
  type DesignLayer,
} from './design-area.js';
import { assertEditable } from './designer-parity.js';

export const DESIGNER_ARRANGEMENTS = [
  { id: 'photo-top', name: 'Slika gore, tekst dole' },
  { id: 'text-top', name: 'Tekst gore, slika dole' },
  { id: 'photo-left', name: 'Slika levo, tekst desno' },
  { id: 'photo-right', name: 'Tekst levo, slika desno' },
  { id: 'overlay', name: 'Tekst preko slike' },
  { id: 'fill', name: 'Popuni polje' },
  { id: 'stack', name: 'Sve na sredini' },
  { id: 'stack-under', name: 'Tekst pa ukras' },
  { id: 'row', name: 'U jednom redu' },
  { id: 'flank', name: 'Ukrasi sa strane' },
] as const;
export type DesignerArrangement = typeof DESIGNER_ARRANGEMENTS[number]['id'];

export interface DesignerArrangeCommand {
  op: 'arrange';
  variant: DesignerArrangement;
  authoring?: boolean;
}

type Role = 'photo' | 'word' | 'decor';

/** One movable unit: a single layer, or a whole group moved as one. */
interface Item {
  role: Role;
  layers: DesignLayer[];
  /** The layers as they were, so placing an item twice lands in the same place. */
  origin: DesignLayer[];
  /** Extent before the move, zone millimetres. */
  left: number;
  top: number;
  w: number;
  h: number;
  /** Largest uniform scale the product's text limits allow. */
  maxScale: number;
  minScale: number;
}

interface Rect { x: number; y: number; w: number; h: number }

/** What an arrangement can work with, for choosing which ones to offer. */
export interface ArrangeCounts { photos: number; words: number; decor: number }

/** A layer the shopper may move: visible, unlocked, interactive, not a tiled pattern. */
function movable(layer: DesignLayer): boolean {
  return layer.visible !== false && !layer.locked && layer.interactive !== false && !layer.pattern;
}

function itemsOf(answer: DesignAreaAnswer, cfg: ReturnType<typeof resolveDesignAreaConfig>): Item[] {
  const units = new Map<string, DesignLayer[]>();
  for (const layer of answer.layers) {
    const key = layer.groupId ? `g:${layer.groupId}` : `l:${layer.id}`;
    units.set(key, [...(units.get(key) ?? []), layer]);
  }
  const items: Item[] = [];
  for (const layers of units.values()) {
    // A group moves whole or not at all.
    if (!layers.every(movable)) continue;
    const role: Role = layers.some(l => l.kind === 'image') ? 'photo' : layers.every(l => l.kind === 'text') ? 'word' : 'decor';
    const extent = layersExtent(layers.length === 1 ? [{ ...layers[0]!, box: { ...layers[0]!.box, rotation: 0 } }] : layers);
    let maxScale = Infinity, minScale = 0;
    for (const layer of layers) if (layer.kind === 'text') {
      maxScale = Math.min(maxScale, cfg.text.maxSizeMm / layer.sizeMm);
      minScale = Math.max(minScale, cfg.text.minSizeMm / layer.sizeMm);
    }
    items.push({ role, layers, origin: structuredClone(layers), left: extent.left, top: extent.top, w: Math.max(0.1, extent.right - extent.left), h: Math.max(0.1, extent.bottom - extent.top), maxScale, minScale });
  }
  return items;
}

export function arrangeCounts(answer: DesignAreaAnswer, config: DesignAreaConfig): ArrangeCounts {
  const items = itemsOf(answer, resolveDesignAreaConfig(config));
  return {
    photos: items.filter(i => i.role === 'photo').length,
    words: items.filter(i => i.role === 'word').length,
    decor: items.filter(i => i.role === 'decor').length,
  };
}

/** The arrangements that make sense for this mix of content, best first. */
export function arrangementsFor(counts: ArrangeCounts): DesignerArrangement[] {
  const { photos, words, decor } = counts;
  const out: DesignerArrangement[] = [];
  if (photos && (words || decor)) out.push('photo-top');
  if (photos && words) out.push('text-top', 'photo-left', 'photo-right', 'overlay');
  if ((photos || decor) && !words) out.push('fill');
  if (!photos && (words || decor)) out.push('stack');
  if (!photos && words && decor) out.push('stack-under', 'row');
  if (!photos && !words && decor > 1) out.push('row');
  if (!photos && words && decor > 1) out.push('flank');
  return [...new Set(out)];
}

// ── Placement ────────────────────────────────────────────────────────────────

/** Moves and uniformly scales one item so its extent is centred in `rect`, at scale `k`. */
function put(item: Item, k: number, cx: number, cy: number): void {
  const ox = item.left + item.w / 2, oy = item.top + item.h / 2;
  item.layers.forEach((layer, index) => {
    const from = item.origin[index]!;
    layer.box = {
      ...from.box,
      x: cx + (from.box.x - ox) * k,
      y: cy + (from.box.y - oy) * k,
      w: from.box.w * k,
      h: from.box.h * k,
      ...(item.layers.length === 1 ? { rotation: 0 } : {}),
    };
    if (layer.kind === 'text' && from.kind === 'text') { layer.sizeMm = from.sizeMm * k; if (from.textWidthMm) layer.textWidthMm = from.textWidthMm * k; }
    if (layer.kind === 'shape' && from.kind === 'shape' && layer.stroke && from.stroke) layer.stroke.widthMm = from.stroke.widthMm * k;
  });
}

/** Clamps a common scale to what every item's text allows (never below the minimum). */
function clampScale(items: Item[], k: number): number {
  const max = Math.min(...items.map(i => i.maxScale));
  const min = Math.max(...items.map(i => i.minScale));
  return Math.max(min, Math.min(k, max));
}

/** A block of items that can report its natural size and place itself in a rect. */
interface Block {
  /** Natural width/height ratio. */
  aspect: number;
  /** Largest share of the free axis this block may take (0–1). */
  cap: number;
  /** Takes the room the fixed blocks leave (photos). */
  flex?: boolean;
  place(rect: Rect): Rect;
}

/** Items one under another (words) or side by side (decor), sharing one scale. */
function lineBlock(items: Item[], axis: 'column' | 'row', gap: number, cap: number): Block {
  const along = (i: Item) => axis === 'column' ? i.h : i.w;
  const across = (i: Item) => axis === 'column' ? i.w : i.h;
  const length = items.reduce((sum, i) => sum + along(i), 0);
  const breadth = Math.max(...items.map(across));
  // Gaps scale with the content, as a fraction of the natural length.
  const gapShare = gap / Math.max(1, length);
  const natural = length * (1 + gapShare * (items.length - 1));
  return {
    aspect: axis === 'column' ? breadth / natural : natural / breadth,
    cap,
    place(rect) {
      const room = axis === 'column' ? rect.h : rect.w, side = axis === 'column' ? rect.w : rect.h;
      const k = clampScale(items, Math.min(side / breadth, (room - gap * (items.length - 1)) / length));
      const used = length * k + gap * (items.length - 1);
      let at = (axis === 'column' ? rect.y + rect.h / 2 : rect.x + rect.w / 2) - used / 2;
      for (const item of items) {
        const size = along(item) * k;
        if (axis === 'column') put(item, k, rect.x + rect.w / 2, at + size / 2);
        else put(item, k, at + size / 2, rect.y + rect.h / 2);
        at += size + gap;
      }
      return axis === 'column'
        ? { x: rect.x + (rect.w - breadth * k) / 2, y: rect.y + (rect.h - used) / 2, w: breadth * k, h: used }
        : { x: rect.x + (rect.w - used) / 2, y: rect.y + (rect.h - breadth * k) / 2, w: used, h: breadth * k };
    },
  };
}

/** Photos (or pictures) in the grid that shows them largest; each fits its own cell. */
function gridBlock(items: Item[], gap: number, cap: number): Block {
  const layoutFor = (w: number, h: number) => {
    let best = { cols: 1, rows: items.length, score: -1 };
    for (let cols = 1; cols <= items.length; cols++) {
      const rows = Math.ceil(items.length / cols);
      const cw = (w - gap * (cols - 1)) / cols, ch = (h - gap * (rows - 1)) / rows;
      if (cw <= 0 || ch <= 0) continue;
      const score = items.reduce((sum, i) => { const k = Math.min(cw / i.w, ch / i.h); return sum + i.w * i.h * k * k; }, 0);
      if (score > best.score) best = { cols, rows, score };
    }
    return best;
  };
  const meanAspect = items.reduce((sum, i) => sum + i.w / i.h, 0) / items.length;
  const cols = Math.ceil(Math.sqrt(items.length));
  const rows = Math.ceil(items.length / cols);
  return {
    aspect: meanAspect * cols / rows,
    cap,
    flex: true,
    place(rect) {
      const { cols, rows } = layoutFor(rect.w, rect.h);
      const cw = (rect.w - gap * (cols - 1)) / cols, ch = (rect.h - gap * (rows - 1)) / rows;
      let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
      items.forEach((item, index) => {
        const col = index % cols, row = Math.floor(index / cols);
        // A short last row is centred rather than left-aligned.
        const inRow = row === rows - 1 ? items.length - row * cols : cols;
        const offset = (cols - inRow) * (cw + gap) / 2;
        const k = clampScale([item], Math.min(cw / item.w, ch / item.h));
        const cx = rect.x + offset + col * (cw + gap) + cw / 2, cy = rect.y + row * (ch + gap) + ch / 2;
        put(item, k, cx, cy);
        left = Math.min(left, cx - item.w * k / 2); right = Math.max(right, cx + item.w * k / 2);
        top = Math.min(top, cy - item.h * k / 2); bottom = Math.max(bottom, cy + item.h * k / 2);
      });
      return { x: left, y: top, w: right - left, h: bottom - top };
    },
  };
}

/**
 * Blocks stacked along one axis. Each is offered the length it would need at
 * the full cross size, capped by its share; if the total is too long, all
 * shrink together. Then the used boxes are packed with one gap between them
 * and the whole stack is centred — no dead band where a block came up short.
 */
function stack(blocks: Block[], rect: Rect, axis: 'column' | 'row', gap: number): Rect {
  if (!blocks.length) return rect;
  const cross = axis === 'column' ? rect.w : rect.h;
  const room = (axis === 'column' ? rect.h : rect.w) - gap * (blocks.length - 1);
  const wanted = blocks.map(b => Math.min(axis === 'column' ? cross / b.aspect : cross * b.aspect, room * b.cap));
  // Words and decor get their share first; a flexible block (the photos) takes
  // whatever is left, so a name is never shrunk to make a picture bigger.
  const fixed = wanted.reduce((sum, length, i) => sum + (blocks[i]!.flex ? 0 : length), 0);
  const flexWanted = wanted.reduce((sum, length, i) => sum + (blocks[i]!.flex ? length : 0), 0);
  const fixedShrink = fixed > room * 0.85 && flexWanted ? room * 0.45 / fixed : fixed > room ? room / fixed : 1;
  const flexRoom = Math.max(0, room - fixed * fixedShrink);
  const flexShrink = flexWanted > flexRoom ? flexRoom / flexWanted : 1;
  let at = 0;
  const placed = blocks.map((block, index) => {
    const length = wanted[index]! * (block.flex ? flexShrink : fixedShrink);
    const slot = axis === 'column' ? { x: rect.x, y: rect.y + at, w: rect.w, h: length } : { x: rect.x + at, y: rect.y, w: length, h: rect.h };
    at += length + gap;
    return { block, slot, used: block.place(slot) };
  });
  // Pack: shift every block so the used boxes touch at one gap, centred.
  const usedLength = placed.reduce((sum, p) => sum + (axis === 'column' ? p.used.h : p.used.w), 0) + gap * (placed.length - 1);
  let cursor = (axis === 'column' ? rect.y + rect.h / 2 : rect.x + rect.w / 2) - usedLength / 2;
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const p of placed) {
    const start = axis === 'column' ? p.used.y : p.used.x;
    const shift = cursor - start;
    const used = p.block.place(axis === 'column' ? { ...p.slot, y: p.slot.y + shift } : { ...p.slot, x: p.slot.x + shift });
    left = Math.min(left, used.x); top = Math.min(top, used.y);
    right = Math.max(right, used.x + used.w); bottom = Math.max(bottom, used.y + used.h);
    cursor += (axis === 'column' ? p.used.h : p.used.w) + gap;
  }
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/** A block made of other blocks, so a column can hold decor over words. */
function nested(blocks: Block[], axis: 'column' | 'row', gap: number, cap: number): Block {
  const aspects = blocks.map(b => b.aspect);
  const aspect = axis === 'column'
    ? 1 / aspects.reduce((sum, a) => sum + 1 / a, 0)
    : aspects.reduce((sum, a) => sum + a, 0);
  return {
    aspect,
    cap,
    place(rect) {
      return stack(blocks, rect, axis, gap);
    },
  };
}

/** Reading order: top to bottom, then left to right. */
function readingOrder(a: Item, b: Item): number {
  const ay = a.top + a.h / 2, by = b.top + b.h / 2;
  return Math.abs(ay - by) > Math.min(a.h, b.h) / 2 ? ay - by : a.left - b.left;
}

export function applyDesignerArrange(config: DesignAreaConfig, answer: DesignAreaAnswer, command: DesignerArrangeCommand): DesignAreaAnswer {
  const cfg = resolveDesignAreaConfig(config);
  const next = structuredClone(answer);
  const items = itemsOf(next, cfg);
  const counts = arrangeCounts(answer, config);
  if (!items.length) throw new Error('Dodajte sliku, tekst ili ukras, pa ih rasporedite.');
  if (!arrangementsFor(counts).includes(command.variant)) throw new Error('Ovaj raspored ne odgovara sadržaju dizajna.');
  assertEditable(items.flatMap(i => i.layers), command.authoring);

  const sticker = answer.sticker;
  const margin = Math.max(cfg.safeMarginMm, 3, sticker ? sticker.borderMm + 1.5 : 0);
  const area: Rect = { x: margin, y: margin, w: cfg.sizeMm.w - margin * 2, h: cfg.sizeMm.h - margin * 2 };
  if (area.w <= 0 || area.h <= 0) throw new Error('Print zone is too small to arrange');
  const gap = Math.max(1.2, Math.min(area.w, area.h) * 0.05);

  const photos = items.filter(i => i.role === 'photo').sort(readingOrder);
  const words = items.filter(i => i.role === 'word').sort(readingOrder);
  const decor = items.filter(i => i.role === 'decor').sort((a, b) => a.left - b.left || a.top - b.top);
  // Words may take a third of a photo layout, all of a words-only one.
  const wordCap = photos.length ? 0.36 : 1;
  const wordBlock = () => lineBlock(words, 'column', gap * 0.5, wordCap);
  const decorRow = (cap: number) => lineBlock(decor, 'row', gap, cap);
  const photoGrid = (cap = 1) => gridBlock(photos, gap, cap);

  switch (command.variant) {
    case 'photo-top':
      stack([photoGrid(), ...(decor.length ? [decorRow(0.16)] : []), ...(words.length ? [wordBlock()] : [])], area, 'column', gap);
      break;
    case 'text-top':
      stack([...(decor.length ? [decorRow(0.16)] : []), wordBlock(), photoGrid()], area, 'column', gap);
      break;
    case 'photo-left':
    case 'photo-right': {
      const side = nested([...(decor.length ? [decorRow(0.25)] : []), lineBlock(words, 'column', gap * 0.5, 1)], 'column', gap, 0.5);
      const pictures = photoGrid(0.6);
      stack(command.variant === 'photo-left' ? [pictures, side] : [side, pictures], area, 'row', gap);
      break;
    }
    case 'overlay': {
      // The pictures fill the zone; words (and decor) sit in its lower third.
      gridBlock(photos, gap, 1).place(area);
      const band: Rect = { x: area.x + gap, y: area.y + area.h * 0.64, w: area.w - gap * 2, h: area.h * 0.32 };
      stack([...(decor.length ? [decorRow(0.3)] : []), lineBlock(words, 'column', gap * 0.3, 1)], band, 'column', gap * 0.5);
      break;
    }
    case 'fill':
      gridBlock([...photos, ...decor], gap, 1).place(area);
      break;
    case 'stack':
      stack([...(decor.length ? [decorRow(words.length ? 0.3 : 1)] : []), ...(words.length ? [wordBlock()] : [])], area, 'column', gap);
      break;
    case 'stack-under':
      stack([wordBlock(), decorRow(0.3)], area, 'column', gap);
      break;
    case 'row':
      if (words.length) stack([decorRow(0.4), lineBlock(words, 'column', gap * 0.5, 1)], area, 'row', gap);
      else decorRow(1).place(area);
      break;
    case 'flank': {
      const half = Math.ceil(decor.length / 2);
      stack([lineBlock(decor.slice(0, half), 'column', gap, 0.22), lineBlock(words, 'column', gap * 0.5, 1), lineBlock(decor.slice(half), 'column', gap, 0.22)], area, 'row', gap);
      break;
    }
  }

  const normalized = normalizeDesignAnswer(next)!;
  const problems = designAreaProblems(config, normalized).filter(p => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(p.code));
  if (problems.length) throw new Error(problems[0]!.detail);
  return normalized;
}

export const DESIGNER_ARRANGE_CAPABILITIES = {
  operation: 'arrange',
  fields: ['variant'],
  variants: DESIGNER_ARRANGEMENTS.map(a => a.id),
  roles: 'photos = image layers and groups containing one; words = text layers and all-text groups (reading order); decor = artwork, emoji, shapes, QR',
  offered: 'arrangementsFor(counts) lists the variants that fit the current content; others are refused',
  scope: 'Keeps every layer and its content/crop/effects; only moves and uniformly scales visible, unlocked, interactive, unpatterned layers (groups move whole). Relative sizes inside a block are preserved; text stays within the product size limits. One undo step; native preview and print.',
} as const;
